import { useAtom } from '@effect/atom-react';
import {
  apiMutation,
  withOptimisticUpdate,
} from '@listening/frontend/query/api-runtime';
import {
  deleteNoteEffect,
  notesAtom,
  notesKeys,
  upsertNotesAtom,
} from '@listening/frontend/query/notes';
import { apiFailureMessage } from '@listening/frontend/query/query-error';
import { notePlaybackAnchorFields } from '@listening/frontend/audio-chunk-player/note-playback';
import { Cause, Effect, Exit } from 'effect';
import { Atom, Reactivity } from 'effect/reactivity';
import { toast } from 'sonner';
import { useReadAlongSidebarStore } from '@/components/start/read-along/read-along-store';
import {
  getDocPackPlaybackAnchorForPosition,
  useAudioChunkPlayer,
  useAudioChunkSelector,
  useCurrentAudioChunkQuery,
} from '@/lib/audio-chunk-player';
import { rootLogger } from '@/lib/root-logger';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { useAuthSession } from '@/services/auth-service';
import { audioService } from '@/services/audio-service';
import * as m from '@/paraglide/messages';

const moduleLogger = rootLogger.child({
  module: 'NotesService',
});

/** One note's delete, so each row shows only its own progress. The note
 * leaves every view at once and comes back if the delete fails. */
const deleteNoteRowAtom = Atom.family((noteId: string) =>
  apiMutation(() =>
    withOptimisticUpdate(
      notesAtom,
      (notes) => notes.filter((note) => note.id !== noteId),
      deleteNoteEffect(noteId),
    ).pipe(Effect.ensuring(Reactivity.invalidate([notesKeys.list]))),
  ),
);

export const useDeleteNoteMutation = (noteId: string) => {
  const { capture } = useAnalytics();
  const [result, deleteNote] = useAtom(deleteNoteRowAtom(noteId), {
    mode: 'promiseExit',
  });
  return {
    isPending: result.waiting,
    deleteNote: async () => {
      const exit = await deleteNote();
      if (Exit.isSuccess(exit)) {
        moduleLogger.info('Note deleted successfully');
        capture('note_pad_interaction', {
          interaction: 'note_deleted',
        });
      }
      return exit;
    },
  };
};

export const useAddAudioNote = () => {
  const audioItem = audioService.store.useCurrentAudio();
  const [upsertResult, upsertNotes] = useAtom(upsertNotesAtom, {
    mode: 'promiseExit',
  });
  const { setSidebarState } = useReadAlongSidebarStore();

  const { user } = useAuthSession();
  const userId = user?.userId;

  const audioChunk = useCurrentAudioChunkQuery().data;
  const chapterId = useAudioChunkSelector((state) => state.chapterId);
  const ttsId = useAudioChunkSelector((state) => state.ttsId);
  const player = useAudioChunkPlayer().player;

  const { capture } = useAnalytics();

  return {
    addAudioNote: () => {
      moduleLogger.info('Adding audio note');
      if (!audioItem) {
        moduleLogger.error('Audio item not found while adding note');
        toast.error(m.toast_note_error_no_audio());
        return;
      }

      if (!userId) {
        moduleLogger.error('User not found while adding note');
        toast.error(m.toast_note_error_not_logged_in());
        return;
      }

      const docPackAnchor =
        audioChunk?.playbackMode === 'doc-pack'
          ? getDocPackPlaybackAnchorForPosition(
              audioChunk,
              player.getPositionMs(),
            )
          : undefined;
      if (!docPackAnchor && !(chapterId && ttsId)) {
        moduleLogger.warn(
          'Could not find a playback anchor while adding audio note, creating custom note instead',
        );
      }
      const noteAnchorFields = notePlaybackAnchorFields({
        chapterId,
        ...(docPackAnchor === undefined ? {} : { docPackAnchor }),
        positionMs: player.getPositionMs(),
        ttsId,
      });

      void upsertNotes([
        {
          audio_conversion_id: audioItem.id,
          title: audioItem.title ?? m.toast_note_default_title(),
          body: audioChunk?.text ?? '',
          user_id: userId,
          ...noteAnchorFields,
        },
      ]).then((exit) => {
        if (Exit.isSuccess(exit)) {
          capture('note_pad_interaction', {
            interaction: 'note_added',
          });
          setSidebarState('notes');
        } else {
          moduleLogger.error(
            { err: Cause.squash(exit.cause) },
            'Failed to add note',
          );
          toast.error(apiFailureMessage(exit.cause) ?? m.toast_note_error());
        }
      });
    },
    isAddingNote: upsertResult.waiting,
  };
};
